import type { CircleHitObject, CirclesChart } from '../../circlesChart.js';
import { MAX_HP, STARTING_HP, type JudgeVerdict } from '../../gameTypes.js';
import type { SliderPath } from '../../osuCurves.js';

export interface CircleResolution {
  hitObjectId: string;
  verdict: JudgeVerdict;
  /** Tap-time minus circle-target-time, signed. */
  diffMs: number;
  /** Distance in osu units between tap position and circle centre. */
  distance: number;
  /** Beat at which the resolution happened (input or auto-miss). */
  beat: number;
  /**
   * Player's combo immediately BEFORE this resolution applied. The
   * client uses this to detect combo breaks per-event (was-N, now-0)
   * without having to reverse-engineer it from `state.combo` after
   * the engine has already reset/incremented it. Necessary because
   * one `step()` can emit several events in a row.
   */
  previousCombo: number;
  /**
   * True when the resolution came from a tap that landed on a visible
   * but not-yet-hittable circle (player clicked during the approach
   * window before the good window opened). Counted as a normal miss
   * for HP / combo / streak purposes; the flag exists purely so the
   * client can show distinct "Too Early!" feedback instead of generic
   * "Miss". Absent on auto-miss (no input), perfect, and good
   * resolutions.
   */
  early?: boolean;
}

/**
 * Why a Circles run ended in failure. Mirrors Pulse's
 * {@link import('../pulse/state.js').DeathReason} so the universal
 * Results overlay can render the same friendly copy regardless of
 * mode.
 */
export type CirclesDeathReason = 'drained';

/**
 * Per-spinner accumulating spin progress. Created lazily the first
 * time a sample lands on a given spinner, mutated in-place as more
 * samples arrive, and read at spinner-resolution time so the engine
 * can credit the final rotation count alongside the existing free
 * `good` resolution.
 *
 * The combo multiplier captured at spin-start is locked for every
 * subsequent radian — that's what keeps the client's per-frame
 * incremental score in sync with the server's per-step batched
 * processing of the same samples.
 */
export interface SpinnerProgress {
  /** Total radians of rotation the player has accumulated, unsigned. */
  rotations: number;
  /**
   * The cursor angle (radians, atan2-style) of the most recent
   * processed sample relative to the playfield centre. `null` until
   * the first sample lands so we don't credit a delta on the very
   * first reading.
   */
  lastAngle: number | null;
  /**
   * Beat of the most recent processed sample. Used to clamp the
   * angular-velocity cap to per-second-of-beats so the cap is
   * BPM-aware (a 60 BPM spinner has the same RPS limit as a 200 BPM
   * spinner — players rotate against the wall clock, not the beat).
   */
  lastSampleBeat: number | null;
  /**
   * Combo multiplier locked at the moment the spinner started
   * receiving samples. Frozen so the client (incrementing per-frame)
   * and the server (batching all samples in `step()`) compute the
   * same total bonus regardless of where they slice the work.
   */
  comboMultiplierAtStart: number;
  /**
   * Integer score already credited for this spinner. Score is
   * computed as `floor(rotations * pointsPerRad * mul) - scoreBanked`
   * each tick so the running total depends only on the unrounded
   * `rotations` accumulator — making spinner score determinism
   * framerate-independent (a 30 fps client and a 60 fps client
   * spinning at the same RPS bank the same total).
   */
  scoreBanked: number;
}

export interface CirclesEngineState {
  beat: number;
  combo: number;
  maxCombo: number;
  perfects: number;
  goods: number;
  /**
   * Count of OK-tier hits (mercy band — within ±okWindowMs but outside
   * ±goodWindowMs). Counted distinctly from goods because accuracy
   * weights them differently (50/300 vs 100/300) and the UI displays
   * the tier explicitly. Modes that don't opt into the OK band (Pulse)
   * leave this at 0 for the entire run.
   */
  oks: number;
  misses: number;
  score: number;
  resolutions: CircleResolution[];
  alive: boolean;
  completed: boolean;
  deathReason: CirclesDeathReason | null;
  deathBeat: number | null;
  /** Hit-object ids that have already been resolved (one resolution per object). */
  resolvedIds: Set<string>;
  hp: number;
  maxHp: number;
  /**
   * Beat of the most recent hit-object resolution that broke the
   * player's combo. Used to gate passive HP regen.
   */
  lastMissBeat: number | null;
  /** See {@link import('../pulse/state.js').EngineState.consecutiveMissCount}. */
  consecutiveMissCount: number;
  /** See {@link import('../pulse/state.js').EngineState.regenProgressBeats}. */
  regenProgressBeats: number;
  /**
   * Per-spinner spin progress, keyed by spinner hit-object id. Grows
   * each time a sample lands on that spinner; entries persist after
   * the spinner resolves so the renderer can fade visuals out without
   * re-querying the resolution event.
   */
  spinnerProgress: Map<string, SpinnerProgress>;
  /**
   * Per-slider tracking progress, keyed by slider hit-object id.
   * Created when the slider's head is tapped (or auto-misses); mutates
   * as cursor samples + tick / repeat / tail beats are crossed.
   * Entries persist after the slider resolves so the renderer can
   * read final verdict + tick counts for trailing visuals.
   */
  sliderProgress: Map<string, SliderProgress>;
}

/**
 * Per-slider accumulating state. Tracks the head verdict, the most
 * recent cursor sample's tracking status (button held + cursor inside
 * follow-circle), and how many ticks / repeats / tail awards have
 * fired so far. Tick / repeat / tail awards are evaluated when
 * advancing time past their respective beats — using `trackingNow` as
 * a proxy for "was the player tracking at that exact beat", since the
 * sample stream is discrete.
 *
 * Slider scoring is per-component (each tick / repeat / tail
 * contributes its own combo+1 + score chunk) rather than aggregating
 * to a single judgement. This matches osu!'s actual behaviour: a
 * partial-track slider credits the components the player did track,
 * doesn't break combo on dropped ticks (our forgiving variant).
 */
export interface SliderProgress {
  /**
   * Verdict from the head tap. null until the head is tapped or
   * auto-misses. Drives the slider's `object_resolved` event payload
   * for accuracy purposes.
   */
  headVerdict: JudgeVerdict | null;
  /**
   * True after the head tap has registered (or auto-missed). Gates
   * tick / repeat / tail evaluation: a slider whose head was never
   * tapped (auto-missed) credits no body components, even if the
   * cursor happened to track through the curve. Matches osu — a
   * dropped head means the slider plays purely visually.
   */
  headResolved: boolean;
  /**
   * Beat of the most-recent cursor sample we processed for this
   * slider. Used to scope tick / repeat checks: "any tick whose beat
   * is in (lastSampleBeat, currentBeat]". null until first sample
   * lands during the slider's window.
   */
  lastSampleBeat: number | null;
  /**
   * Snapshot of the most-recent sample's `(pressing && in_follow_circle)`
   * predicate. Tick / repeat / tail awards consult this when advancing
   * time past their beat — close enough to "tracking at the beat
   * itself" for typical 60Hz sample rates and head/tail beat-spacing.
   */
  trackingNow: boolean;
  /** Number of slider ticks awarded so far. */
  ticksHit: number;
  /** Number of repeat-edges awarded so far. */
  repeatsHit: number;
  /**
   * Whether the tail award (cursor in follow-circle at endBeat)
   * fired AND the player was tracking at that moment. Mirrors
   * `ticksHit`'s "credit only counts when tracking" semantics —
   * doesn't gate event emission, just records score outcome.
   */
  tailHit: boolean;
  /**
   * Whether the tail event has been emitted by the engine (one-shot
   * flag, separate from {@link tailHit}). Prevents double-firing
   * when multiple samples land at or after `endBeat`. The renderer
   * reads `tailHit` to know if the credit fired and `tailFired` to
   * know if the slider's lifecycle is done.
   */
  tailFired: boolean;
  /**
   * Index of the next entry in `slider.tickBeats` we'll evaluate.
   * Engine advances this monotonically as ticks are crossed; never
   * rewinds (would corrupt scoring on out-of-order sample arrival,
   * which the engine pre-sorts).
   */
  nextTickIdx: number;
  /** Index of the next entry in `slider.repeatBeats` we'll evaluate. */
  nextRepeatIdx: number;
  /**
   * Combo multiplier locked at the moment the head tap registered
   * (mirrors {@link SpinnerProgress.comboMultiplierAtStart}). Used
   * to scale per-tick / repeat / tail score awards, so the live
   * client and the headless replay produce identical totals
   * regardless of whether mid-slider hits would have bumped the
   * multiplier.
   */
  comboMultiplierAtHead: number;
}

export interface CirclesEngineConfig {
  chart: CirclesChart;
  /**
   * Hit objects sorted by `(beat asc, id asc)`. The engine reads this
   * — never `chart.hitObjects` directly — so replay determinism does
   * not depend on the JSON producer's array order. Built once via
   * {@link buildHitObjectLookup} when the config is constructed.
   */
  sortedHitObjects: ReadonlyArray<CircleHitObject>;
  /**
   * Perfect timing window in milliseconds. May be sourced from the
   * chart's per-variant override or from the global default —
   * resolved at config-build time so the engine and client renderer
   * use the same value without each re-deriving it.
   */
  perfectWindowMs: number;
  /** Good timing window in milliseconds. See {@link perfectWindowMs}. */
  goodWindowMs: number;
  /**
   * OK timing window in milliseconds. Sourced from
   * `chart.okWindowMs`, falling back to the global `OK_WINDOW_MS`
   * default. Always at least `goodWindowMs` (clamped at config-build
   * time): a chart override below the good window would be invalid
   * since the OK tier sits strictly outside the good window.
   */
  okWindowMs: number;
  /** Perfect window in beats (derived from {@link perfectWindowMs} + chart.bpm). */
  perfectWindowBeats: number;
  /** Good window in beats. */
  goodWindowBeats: number;
  /** OK window in beats. */
  okWindowBeats: number;
  /**
   * Approach time in milliseconds — how long a hit object is visible
   * before its target beat. Decoupled from BPM so authors can ship
   * the same `approachBeats` feel across different tempos (or tune a
   * specific chart wall-clock value via `chart.approachMs`). Default
   * matches the legacy "approachBeats * msPerBeat" so existing charts
   * keep their current feel.
   */
  approachMs: number;
  /** Approach time in beats (derived from `approachMs` + chart.bpm). */
  approachBeats: number;
  /**
   * Pre-built arc-length-parameterised path per slider, keyed by
   * slider hit-object id. Built once at config-construction so the
   * per-frame `pointAtDistance` / `distanceSqToPath` queries don't
   * re-tessellate the curve on every cursor sample. Sliders without
   * a payload (e.g. the auto-degrade-to-circle path in the importer)
   * are absent from the map; the engine only reads from it for known
   * slider ids.
   */
  sliderPaths: ReadonlyMap<string, SliderPath>;
}

export function createCirclesInitialState(_chart: CirclesChart): CirclesEngineState {
  return {
    beat: 0,
    combo: 0,
    maxCombo: 0,
    perfects: 0,
    goods: 0,
    oks: 0,
    misses: 0,
    score: 0,
    resolutions: [],
    alive: true,
    completed: false,
    deathReason: null,
    deathBeat: null,
    resolvedIds: new Set(),
    hp: STARTING_HP,
    maxHp: MAX_HP,
    lastMissBeat: null,
    consecutiveMissCount: 0,
    regenProgressBeats: 0,
    spinnerProgress: new Map(),
    sliderProgress: new Map(),
  };
}

/**
 * Sort hit objects by beat ascending so the step function can scan
 * forward without re-searching the whole list every tick. The chart
 * validator already enforces unique ids; this just imposes a
 * deterministic order on equal-beat ties (id-string lexicographic) so
 * replay verification is repeatable.
 */
export function buildHitObjectLookup(hitObjects: ReadonlyArray<CircleHitObject>): CircleHitObject[] {
  return [...hitObjects].sort((a, b) => {
    if (a.beat !== b.beat) {
      return a.beat - b.beat;
    }
    return a.id.localeCompare(b.id);
  });
}

/**
 * Reset the chart-relative parts of a Circles engine state so the next
 * loop sees a fresh chart but keeps the player's accumulated
 * score/combo/verdict counters. Mirrors {@link
 * import('../pulse/state.js').resetEngineStateForNextLoop} for the
 * Circles state shape.
 *
 * Used by both the live client session and the server-side headless
 * runner — they MUST agree on what carries over, or replay validation
 * drifts as soon as it re-simulates a multi-loop run.
 *
 * Mutates the passed state in place.
 */
export function resetCirclesEngineStateForNextLoop(state: CirclesEngineState): void {
  state.beat = 0;
  state.resolutions = [];
  state.alive = true;
  state.completed = false;
  state.deathReason = null;
  state.deathBeat = null;
  state.resolvedIds = new Set();
  state.hp = state.maxHp;
  state.lastMissBeat = null;
  state.consecutiveMissCount = 0;
  state.regenProgressBeats = 0;
  state.spinnerProgress = new Map();
  state.sliderProgress = new Map();
  // Note: score, combo, maxCombo, perfects, goods, oks, misses are
  // intentionally preserved — they accumulate across loops so the
  // results overlay can show the run total. Spinner / slider
  // progress is chart-relative (each loop is a fresh chart traversal
  // with the same ids becoming active again) so the maps reset too.
}

